import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiClient } from 'utils/apiClient';

const ResaleCheckoutModal = ({ isOpen, onClose, resaleContext }) => {
  const [product, setProduct] = useState(null);
  const [prices, setPrices] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Shared promise ref to prevent duplicate calls
  const loadPromiseRef = useRef(null);

  const loadProductData = useCallback(async () => {
    if (!resaleContext?.resale_id) return;
    
    // Return existing promise if already loading (shared promise pattern)
    if (loadPromiseRef.current) {
      return loadPromiseRef.current;
    }
    
    // Create single shared promise
    loadPromiseRef.current = (async () => {
      try {
        setLoading(true);
        setError('');

        const apiClient = getApiClient();
      
      // Get resale prices
      const pricesResponse = await apiClient.get(`/api/v1/resale/${resaleContext.resale_id}/prices`);
      if (pricesResponse.success) {
        setPrices(pricesResponse.prices);
      } else {
        setError('Failed to load product pricing');
      }

      // Get resale config for product descriptions
      const configResponse = await apiClient.get(`/api/v1/resale/${resaleContext.resale_id}/config`);
      if (configResponse.success) {
        const config = configResponse.config;
        const productDescriptions = config.product_descriptions || {};
        
        // Create product object
        setProduct({
          id: resaleContext.preselected_product,
          name: 'B-esper Product',
          description: productDescriptions[resaleContext.preselected_product] || 'AI-powered solution for your business'
        });
      }

    } catch (err) {
      console.error('Error loading product data:', err);
      setError('Failed to load product information');
    } finally {
      setLoading(false);
      loadPromiseRef.current = null; // Clear promise reference
    }
    })();
    
    return loadPromiseRef.current;
  }, [resaleContext?.resale_id]);

  useEffect(() => {
    if (isOpen && resaleContext) {
      loadProductData();
    }
  }, [isOpen, resaleContext, loadProductData]);

  const handleCheckout = async () => {
    try {
      setProcessing(true);
      setError('');

      const apiClient = getApiClient();
      
      // Get the selected price
      const productPrices = prices[resaleContext.preselected_product];
      if (!productPrices || !productPrices[selectedPeriod]) {
        setError('Selected pricing option not available');
        return;
      }

      const priceId = productPrices[selectedPeriod].price_id;
      
      // Create Stripe checkout session
      const checkoutResponse = await apiClient.post('/v1/payments/create-checkout-session', {
        price_id: priceId,
        success_url: window.location.origin + '/?checkout=success',
        cancel_url: window.location.origin + '/?checkout=cancelled'
      });

      if (checkoutResponse.success) {
        // Redirect to Stripe checkout
        window.location.href = checkoutResponse.checkout_url;
      } else {
        setError(checkoutResponse.error || 'Failed to create checkout session');
      }

    } catch (err) {
      console.error('Error during checkout:', err);
      setError('Checkout failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getSelectedPrice = () => {
    if (!product || !prices[product.id]) return null;
    return prices[product.id][selectedPeriod];
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal resale-checkout-modal">
        <div className="modal-header">
          <h2>Complete Your Purchase</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">Loading product information...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : product ? (
            <>
              <div className="product-info">
                <h3>{product.name}</h3>
                <p className="product-description">{product.description}</p>
              </div>

              <div className="billing-options">
                <h4>Choose Billing Period</h4>
                <div className="billing-toggles">
                  <button
                    className={`billing-toggle ${selectedPeriod === 'month' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('month')}
                  >
                    Monthly
                  </button>
                  <button
                    className={`billing-toggle ${selectedPeriod === 'year' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('year')}
                  >
                    Annual
                  </button>
                </div>
              </div>

              {getSelectedPrice() && (
                <div className="price-summary">
                  <div className="price-display">
                    <span className="price-amount">{formatPrice(getSelectedPrice().amount)}</span>
                    <span className="price-period">per {selectedPeriod === 'month' ? 'month' : 'year'}</span>
                  </div>
                  {selectedPeriod === 'year' && (
                    <div className="savings-note">
                      Save 2 months compared to monthly billing
                    </div>
                  )}
                </div>
              )}

              <div className="checkout-actions">
                <button
                  className="btn-primary checkout-button"
                  onClick={handleCheckout}
                  disabled={processing || !getSelectedPrice()}
                >
                  {processing ? 'Processing...' : 'Continue to Payment'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={onClose}
                  disabled={processing}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="error">Product not found</div>
          )}
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .resale-checkout-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          padding: 24px 24px 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          margin: 0;
          color: #333;
          font-size: 24px;
          font-weight: 600;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          padding: 4px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .modal-close:hover {
          background: #f0f0f0;
        }

        .modal-body {
          padding: 24px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 16px;
        }

        .product-info {
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e1e5e9;
        }

        .product-info h3 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 20px;
          font-weight: 600;
        }

        .product-description {
          margin: 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        }

        .billing-options {
          margin-bottom: 24px;
        }

        .billing-options h4 {
          margin: 0 0 16px 0;
          color: #333;
          font-size: 16px;
          font-weight: 600;
        }

        .billing-toggles {
          display: flex;
          gap: 8px;
        }

        .billing-toggle {
          flex: 1;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: 6px;
          background: white;
          color: #666;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .billing-toggle:hover {
          border-color: #003057;
        }

        .billing-toggle.active {
          background: #003057;
          color: white;
          border-color: #003057;
        }

        .price-summary {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
          text-align: center;
        }

        .price-display {
          margin-bottom: 8px;
        }

        .price-amount {
          font-size: 32px;
          font-weight: 700;
          color: #003057;
        }

        .price-period {
          font-size: 16px;
          color: #666;
          margin-left: 8px;
        }

        .savings-note {
          font-size: 14px;
          color: #28a745;
          font-weight: 500;
        }

        .checkout-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .checkout-button {
          background: #003057;
          color: white;
          border: none;
          padding: 16px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .checkout-button:hover:not(:disabled) {
          background: #002244;
        }

        .checkout-button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
          border: none;
          padding: 12px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #5a6268;
        }

        .btn-secondary:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .resale-checkout-modal {
            width: 95%;
            margin: 20px;
          }
          
          .modal-header,
          .modal-body {
            padding: 20px;
          }
          
          .price-amount {
            font-size: 28px;
          }
        }
      `}</style>
    </div>
  );
};

export default ResaleCheckoutModal;


